import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { Text } from "../../ui/text";
import MontuMarkdown from "../markdown";
import ConceptMessage from "./answers/form/ConceptMessage";
import FormMessage from "./answers/form/FormMessage";
import MontuEmblem from "./MontuEmblem";
import type { AgentChatMessage } from "./types";

type Props = {
  message: AgentChatMessage;
  /** The next message is from the same side — tighten the gap and drop the tail. */
  grouped?: boolean;
};

type TextBubbleProps = {
  role: AgentChatMessage["role"];
  text: string;
  grouped: boolean;
  pending?: boolean;
};

/**
 * The assistant's text is markdown — the model writes lists, bold and headings and they
 * should render as such. The user's own text is not: it is whatever they typed, and
 * rendering it as markdown would mangle a stray asterisk.
 */
const TextBubble = ({
  role,
  text,
  grouped,
  pending = false,
}: TextBubbleProps) => {
  const gap = grouped ? "mb-1" : "mb-3";

  if (role === "user") {
    return (
      <Box
        className={`${gap} max-w-[72%] self-end rounded-2xl ${
          grouped ? "" : "rounded-br-md"
        } bg-brand-500 px-3.5 py-2.5 animate-fade-rise motion-reduce:animate-none ${pending ? "opacity-60" : ""}`}
      >
        <Text className="text-sm leading-5 text-typography-white">{text}</Text>
      </Box>
    );
  }

  return (
    // The avatar anchors the agent's identity to the LEFT of its bubbles — only on
    // the first of a group, so a run of consecutive replies reads as one voice
    // rather than a column of repeated faces. Grouped follow-ups indent under it
    // instead, via `ml-7`, so the text still lines up with the one avatar above it.
    <HStack
      className={`${gap} items-start animate-fade-rise motion-reduce:animate-none`}
    >
      {!grouped ? (
        <Box className="mr-2 mt-0.5">
          <MontuEmblem size={22} />
        </Box>
      ) : null}
      <Box
        className={`min-w-0 flex-1 rounded-2xl ${grouped ? "ml-7" : ""} ${
          grouped ? "" : "rounded-bl-md"
        } bg-background-50 px-3.5 py-2.5`}
      >
        <MontuMarkdown baseSize={14}>{text}</MontuMarkdown>
      </Box>
    </HStack>
  );
};

/**
 * One settled turn.
 *
 * Form-first rows (a FORM card, a submitted form, a concept card) are their own
 * components, checked first. A question row of an old interview-style chat renders as
 * two plain bubbles — the prompt, then the answer — and only the row that carries an
 * answer does, so a question is never printed twice; the row that only asked shows
 * whatever prose came with it.
 */
const MessageBubble = ({ message, grouped = false }: Props) => {
  // Form-first rows: a FORM card (live or submitted) and a concept card. Cards the
  // backend posts, not text the model wrote — the row's `text` is only what the model
  // reads back when the thread is replayed.
  if (message.form) return <FormMessage message={message} />;
  if (message.concept) return <ConceptMessage message={message} />;

  if (message.legacy?.answer) {
    return (
      <>
        <TextBubble
          role="assistant"
          text={message.legacy.prompt}
          grouped={false}
        />
        <TextBubble role="user" text={message.legacy.answer} grouped={false} />
      </>
    );
  }

  return (
    <TextBubble
      role={message.role}
      text={message.text}
      grouped={grouped}
      pending={message.pending}
    />
  );
};

export default MessageBubble;
